/**
 * Moto Locations Page
 * ===================
 * Manage motorcycle parts storage locations.
 */

import { useState } from 'react'
import { Plus, Edit2, Trash2, MapPin, AlertCircle, X } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import Input from '@/components/Input'
import {
  useMotoLocations,
  useCreateMotoLocation,
  useDeleteMotoLocation,
  useUpdateMotoLocation,
} from '@/hooks/useMotoLocations'
import type { MotoLocation, MotoLocationCreate } from '@/types'

export default function MotoLocations() {
  const { data, isLoading, error } = useMotoLocations()
  const createLocation = useCreateMotoLocation()
  const updateLocation = useUpdateMotoLocation()
  const deleteLocation = useDeleteMotoLocation()

  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formData, setFormData] = useState<MotoLocationCreate>({ name: '' })
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const locations = data?.data ?? []

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) return

    if (editingId) {
      await updateLocation.mutateAsync({ id: editingId, data: formData })
      setEditingId(null)
    } else {
      await createLocation.mutateAsync(formData)
      setIsAdding(false)
    }
    setFormData({ name: '' })
  }

  const handleEdit = (location: MotoLocation) => {
    setEditingId(location.id)
    setFormData({ name: location.name, description: location.description })
    setIsAdding(false)
  }

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this location?')) {
      setDeleteError(null)
      try {
        await deleteLocation.mutateAsync(id)
      } catch (error: unknown) {
        const err = error as { response?: { data?: { detail?: string } } }
        const message = err.response?.data?.detail || 'Failed to delete location'
        setDeleteError(message)
      }
    }
  }

  const handleCancel = () => {
    setIsAdding(false)
    setEditingId(null)
    setFormData({ name: '' })
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error loading locations</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Moto Locations</h1>
          <p className="text-gray-500 dark:text-gray-400">
            Define where your motorcycle parts are stored
          </p>
        </div>
        <Button icon={<Plus size={16} />} onClick={() => setIsAdding(true)}>
          Add Location
        </Button>
      </div>

      {/* Delete error message */}
      {deleteError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-red-800 dark:text-red-200 font-medium">Cannot delete location</p>
            <p className="text-red-600 dark:text-red-300 text-sm mt-1">{deleteError}</p>
          </div>
          <button
            onClick={() => setDeleteError(null)}
            className="text-red-500 hover:text-red-700 dark:hover:text-red-300"
          >
            <X size={20} />
          </button>
        </div>
      )}

      {/* Add/Edit form */}
      {(isAdding || editingId) && (
        <Card>
          <CardHeader title={editingId ? 'Edit Location' : 'New Location'} />
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Location Name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Garage Shelf A"
                required
              />
              <Input
                label="Description (optional)"
                value={formData.description ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value || null,
                  }))
                }
                placeholder="Brief description of this location"
              />
              <div className="flex gap-2">
                <Button
                  type="submit"
                  loading={createLocation.isPending || updateLocation.isPending}
                >
                  {editingId ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Locations list */}
      <Card>
        <CardHeader title="All Moto Locations" />
        <div className="divide-y dark:divide-gray-700">
          {isLoading ? (
            <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : locations.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <MapPin className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={48} />
              <p className="text-gray-500 dark:text-gray-400">No moto locations yet</p>
              <button
                className="text-primary-600 dark:text-primary-400 hover:underline mt-2"
                onClick={() => setIsAdding(true)}
              >
                Create your first location
              </button>
            </div>
          ) : (
            locations.map((location) => (
              <div
                key={location.id}
                className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{location.name}</p>
                      {location.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {location.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(location)}>
                    <Edit2 size={16} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(location.id)}>
                    <Trash2 size={16} className="text-red-500" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  )
}
